import React, { useEffect, useState, useCallback } from "react";
import {
  Space,
  message,
  Table,
  Button,
  Typography,
  Popconfirm,
  Modal,
} from "antd";
import Util from "../../Util";
import { PAYMENT_STATUS, downloadAsCsv } from "./CommonUtils";

const { Text, Link } = Typography;

function PaymentNoVendorId() {
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const [noVendorIdTable, setNoVendorIdTable] = useState([]);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [detailModalRow, setDetailModalRow] = useState(null);
  const [tablePagination, setTablePagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const loadNoVendorIdData = useCallback(async () => {
    setLoading(true);
    const data = await Util.sendGet("/api/moresheetStaff/getHytextsTopay", {});
    if (!data.success) {
      messageApi.error("มีข้อผิดพลาด");
      setLoading(false);
      return;
    }
    const payments = data.data.payments.sort((a, b) => {
      if (a.year < b.year) return -1;
      if (a.year > b.year) return 1;
      if (a.month === b.month) return 0;
      return a.month < b.month ? -1 : 1;
    });
    const relaventSellers = data.data.relaventSellers;

    // Group by facebookID
    const facebookIDGroup = {}; // 100% revenue, need to calculate 60%
    for (const row of payments) {
      if (!facebookIDGroup.hasOwnProperty(row.author)) {
        facebookIDGroup[row.author] = {
          total: 0,
          paymentIds: [],
          paymentDetail: [],
        };
      }
      facebookIDGroup[row.author].total += row.total;
      facebookIDGroup[row.author].paymentIds.push(row._id);
      facebookIDGroup[row.author].paymentDetail.push({
        month: `${row.year}-${row.month}`,
        total: row.total.toFixed(2),
        payout: (row.total * 0.6).toFixed(2),
      });
    }

    // Group by Vendor ID / split those without
    const noVendorIdTable = [];

    for (const seller of relaventSellers) {
      if (!facebookIDGroup.hasOwnProperty(seller.facebookID)) continue;
      const { total, paymentIds, paymentDetail } =
        facebookIDGroup[seller.facebookID];
      const payout = total * 0.6;
      if (!seller.htiVendorId) {
        noVendorIdTable.push({
          codeName: seller.codeName,
          facebookID: seller.facebookID,
          paymentIds,
          peopleID: seller.peopleID,
          realName: seller.realName,
          revenue: total.toFixed(2),
          payout: payout.toFixed(2),
          paymentDetail,
        });
        continue;
      }
    }

    setLoading(false);
    return noVendorIdTable;
  }, [messageApi]);

  useEffect(() => {
    async function fetchData() {
      const noVendorIdTable = await loadNoVendorIdData();
      setNoVendorIdTable(noVendorIdTable);
    }
    fetchData();
  }, [loadNoVendorIdData]);

  const updateStatus = async () => {
    setLoading(true);
    const result = await Util.sendPost(
      "/api/moresheetStaff/updatePaymentStatusByIds",
      {
        ids: noVendorIdTable.flatMap((row) => row.paymentIds),
        paymentStatus: PAYMENT_STATUS.PAUSED,
        paymentPausedReason: "ข้อมูลผู้ขายไม่ครบถ้วน",
      }
    );
    setLoading(false);
    if (result.success) {
      messageApi.success(`อัพเดตสถานะสำเร็จ ${result.modifiedCount} รายการ`);
      // Reload data to reflect changes
      const noVendorIdTable = await loadNoVendorIdData();
      setNoVendorIdTable(noVendorIdTable);
    } else {
      messageApi.error(
        `อัพเดตสถานะไม่สำเร็จ: ${result.message || "เกิดข้อผิดพลาด"}`
      );
    }
  };

  const showDetailModal = (row) => {
    setDetailModalRow(row);
    setDetailModalVisible(true);
  };

  const closeDetailModal = () => {
    setDetailModalVisible(false);
  };

  const handleTableChange = (pagination) => {
    setTablePagination(pagination);
  };

  const VendorTables = () => {
    const noVendorColumns = [
      { title: "นามปากกา", dataIndex: "codeName", key: "codeName" },
      { title: "เลขบัตรประชาชน", dataIndex: "peopleID", key: "peopleID" },
      { title: "ชื่อจริง", dataIndex: "realName", key: "realName" },
      {
        title: "ยอดขาย",
        dataIndex: "revenue",
        key: "revenue",
        render: (text, row) => (
          <Link onClick={() => showDetailModal(row)}>{text}</Link>
        ),
      },
      {
        title: "ยอดจ่าย(60%)",
        dataIndex: "payout",
        key: "payout",
        render: (text, row) => (
          <Link onClick={() => showDetailModal(row)}>{text}</Link>
        ),
      },
    ];

    return (
      <React.Fragment>
        <Button
          type="primary"
          onClick={() =>
            downloadAsCsv(
              noVendorIdTable,
              noVendorColumns,
              "no_vendorId_payments.csv"
            )
          }
        >
          Download as CSV
        </Button>
        <br />

        <Popconfirm
          title="Confirm"
          description="เปลี่ยนสถานะรายการทั้งหมดเป็น 'ข้อมูลผู้ขายไม่ครบถ้วน'"
          onConfirm={updateStatus}
          onCancel={() => {}}
          okText="เปลี่ยนสถานะ"
          cancelText="ยกเลิก"
        >
          <Button type="primary" danger>
            ปรับสถานะเป็น "ข้อมูลผู้ขายไม่ครบถ้วน"
          </Button>
        </Popconfirm>
        <Table
          dataSource={noVendorIdTable}
          columns={noVendorColumns}
          pagination={{
            ...tablePagination,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100", "200"],
            total: noVendorIdTable.length,
          }}
          onChange={handleTableChange}
          rowKey="facebookID"
        />
      </React.Fragment>
    );
  };

  return (
    <Space direction="vertical" size={12}>
      {contextHolder}
      <Modal
        title="รายละเอียดยอดขายสะสม"
        open={detailModalVisible}
        onOk={closeDetailModal}
        onCancel={closeDetailModal}
        footer={[
          <Button key="ok" type="primary" onClick={closeDetailModal}>
            OK
          </Button>,
        ]}
      >
        <p>
          {detailModalRow?.codeName}: {detailModalRow?.realName}
        </p>
        <Table
          dataSource={detailModalRow?.paymentDetail}
          columns={[
            { title: "เดือน", dataIndex: "month", key: "month" },
            { title: "ยอดขาย", dataIndex: "total", key: "total" },
            {
              title: "ยอดจ่าย (60%)",
              dataIndex: "payout",
              key: "payout",
            },
          ]}
          pagination={false}
          size="small"
        />
        <p>รวมยอดจ่าย {detailModalRow?.payout} บาท</p>
      </Modal>
      {loading ? <Text>Loading...</Text> : <VendorTables />}
    </Space>
  );
}

export default PaymentNoVendorId;
